import { Callout, Dialog, Intent, Size } from '@pinpointhq/thumbtack';
import * as React from 'react';
import { jobPostingApplicationFormDraftSubmissionsPath } from '../../../../javascript/application/ts_routes';
import { getCsrfToken } from '../../../shared/thumbtack';

interface IIntlStrings {
  save_draft_application: Record<
    | 'callout_error_text'
    | 'callout_success_link_text'
    | 'callout_success_text'
    | 'link_modal_email_label'
    | 'link_modal_email_placeholder'
    | 'modal_body'
    | 'modal_button'
    | 'modal_header'
    | 'open_modal_link'
    | 'open_modal_link_text'
    | 'unsaved_fields_warning',
    string
  >;
}

interface INewProps {
  draftUuid: string;
  intl: IIntlStrings;
  jobPostingUuid: string;
  locale: string;
  structuredSectionsTitles: string[];
}

export default function New({ draftUuid, intl, jobPostingUuid, locale, structuredSectionsTitles }: INewProps) {
  const [isLoading, setIsLoading] = React.useState(false);
  const [isOpen, setIsOpen] = React.useState(false);
  const [uuid, setUuid] = React.useState(draftUuid);

  React.useEffect(() => {
    const submitButton = document.querySelector('form button[type="submit"]') as HTMLButtonElement;
    if (submitButton) {
      submitButton.disabled = isLoading;
    }
  }, [isLoading]);

  const closeDialog = React.useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const handleButtonClick = React.useCallback(() => {
    const visibleUploadersProgress: HTMLCollection = document.getElementsByClassName('file-field__progress');
    const uploadersInProgress = Array.from(visibleUploadersProgress).filter((element: HTMLElement) => {
      return !['0%', '100%'].includes(element.style.width);
    });

    if (uploadersInProgress.length > 0) {
      const firstNode = uploadersInProgress[0];
      firstNode.setAttribute('tabIndex', '-1');
      (firstNode as HTMLElement).focus();
      firstNode.removeAttribute('tabIndex');
    } else {
      setIsOpen(true);
    }
  }, [setIsOpen]);

  return (
    <>
      <div className="mar-b-2">
        <span id="application-save-later">{intl.save_draft_application.open_modal_link_text} </span>
        <button
          type="button"
          id="application-save-later-link"
          className="external-panel__link"
          onClick={handleButtonClick}
          style={{ background: 'transparent', border: 'none', padding: 0, margin: 0 }}
        >
          {intl.save_draft_application.open_modal_link}
        </button>
      </div>
      {isOpen ? (
        <SaveDraftApplicationDialog
          closeDialog={closeDialog}
          intl={intl}
          locale={locale}
          jobPostingUuid={jobPostingUuid}
          setIsLoading={setIsLoading}
          uuid={uuid}
          setUuid={setUuid}
          structuredSectionsTitles={structuredSectionsTitles}
        />
      ) : null}
    </>
  );
}

function SuccessfulCallout({ url, intl }: { url: string; intl: IIntlStrings }) {
  return (
    <p className="external-form__text">
      {intl.save_draft_application.callout_success_text}{' '}
      <a href={url} style={{ textDecoration: 'underline' }} data-testid="callout_success_link">
        {intl.save_draft_application.callout_success_link_text}
      </a>
      .
    </p>
  );
}

interface ISaveDraftApplicationDialogProps {
  closeDialog: () => void;
  intl: IIntlStrings;
  jobPostingUuid: string;
  locale: string;
  setIsLoading: (isLoading: boolean) => void;
  setUuid: (uuid: string) => void;
  uuid: string;
  structuredSectionsTitles?: string[];
}

function SaveDraftApplicationDialog({
  closeDialog,
  intl,
  jobPostingUuid,
  locale,
  setIsLoading,
  uuid,
  setUuid,
  structuredSectionsTitles,
}: ISaveDraftApplicationDialogProps) {
  const [calloutComponent, setCalloutComponent] = React.useState<React.ReactNode>(null);
  const [email, setEmail] = React.useState('');

  // Run on mount to try and set email from the email value from job form.
  React.useEffect(() => {
    const inputValue = document.querySelector<HTMLInputElement>(
      'form input[name="application_form[application][email]"]',
    )?.value;
    if (inputValue) {
      setEmail(inputValue);
    }
  }, []);

  const handleSaveDraftApplication = React.useCallback(
    (e) => {
      e.preventDefault();
      const form = document.querySelector('form');
      const formData = new FormData(form);
      setIsLoading(true);
      setCalloutComponent(null);

      formData.append('draft_uuid', uuid);
      formData.append('send_to_address', email);

      fetch(jobPostingApplicationFormDraftSubmissionsPath(jobPostingUuid, { locale }), {
        body: formData,
        headers: {
          'X-CSRF-Token': getCsrfToken(),
        },
        method: 'POST',
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.url) {
            setCalloutComponent(<SuccessfulCallout url={data.url} intl={intl} />);
            setUuid(data.uuid);
          } else if (data.errors) {
            setCalloutComponent(
              // tslint:disable-next-line:jsx-wrap-multiline
              <>
                <p className="external-form__text">{intl.save_draft_application.callout_error_text}</p>
                <ul>
                  {data.errors.map((error) => (
                    <li key={error}>{error}</li>
                  ))}
                </ul>
              </>,
            );
          } else if (data.error) {
            throw new Error(data.error);
          }
        })
        .catch((error) => {
          setCalloutComponent(<p className="external-form__text">{error.message}</p>);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [email, intl, jobPostingUuid, setCalloutComponent, setIsLoading, setUuid, uuid],
  );

  const handleEmailChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setEmail(event.target.value);
    },
    [setEmail],
  );

  return (
    <Dialog
      isOpen={true}
      title=""
      size={Size.MEDIUM}
      onClose={closeDialog}
      canEscapeKeyClose={true}
      canOutsideClickClose={true}
    >
      {calloutComponent ? (
        <div>{calloutComponent}</div>
      ) : (
        <form action="#" onSubmit={handleSaveDraftApplication} className="external-form">
          <h2 className="external-panel__title">{intl.save_draft_application.modal_header}</h2>
          <p className="external-form__text">{intl.save_draft_application.modal_body}</p>
          <div className="mar-b-3">
            <label htmlFor="" className="external-form__label">
              {intl.save_draft_application.link_modal_email_label}
            </label>
            <input
              type="text"
              name="email"
              value={email}
              onChange={handleEmailChange}
              placeholder={intl.save_draft_application.link_modal_email_placeholder}
              required={true}
            />
          </div>
          {structuredSectionsTitles?.length > 0 && (
            <Callout intent={Intent.WARNING} className="mar-b-5">
              <p>{intl.save_draft_application.unsaved_fields_warning}</p>
              <ul>
                {structuredSectionsTitles.map((title) => (
                  <li key={title}>{title}</li>
                ))}
              </ul>
            </Callout>
          )}
          <ExternalButton type="submit">{intl.save_draft_application.modal_button}</ExternalButton>
        </form>
      )}
    </Dialog>
  );
}

const buttonClasses = [
  'external-button',
  'external-button--theme-highlight',
  'theme-highlight-background-color-ie-override',
  'theme-highlight-border-color-ie-override',
];

function ExternalButton({ children, className, ...otherProps }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button className={[...buttonClasses, className].join(' ')} {...otherProps}>
      {children}
    </button>
  );
}
